/**
 * §P.1 · Mandar la invitación por WhatsApp · 27-10-2026
 *
 * El correo de invitación ya salió al añadir al tutor, y sigue valiendo. Esto da
 * **otro enlace** de la misma invitación y abre WhatsApp con el mensaje escrito, para
 * que el club lo mande desde su propio número: a la familia le llega de alguien que
 * conoce, y a nadie le cuesta nada. Kiap no manda el mensaje ni guarda el número.
 *
 * ## El enlace se pide al abrir, no al pulsar
 *
 * «Abrir WhatsApp» abre otra pestaña, y un navegador solo deja hacerlo **en el mismo
 * clic**: si antes hubiera que esperar a la API, Safari lo bloquearía como ventana
 * emergente. Así que el enlace se pide al abrir este recuadro y el botón ya lo tiene.
 * Pedirlo de más no hace daño: cada enlace vale lo mismo y caduca solo.
 *
 * ## Cuando no se puede
 *
 * El servidor no da enlace si la cuenta de esa persona ya se usaba —en otro club, o
 * con contraseña—, porque el enlace abre sesión con ella y aquí lo ve el club. Lo que
 * se enseña entonces es su mensaje: la invitación va solo por correo. Y si ya ha
 * entrado mientras tanto, se recarga la lista para que lo diga.
 */
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi } from '@/shared/api/errores';
import { Cargando, Error as PanelError } from '@/shared/ui/Estados';
import { nombreDeTutor, type Tutor } from '@/club/alumnos/tipos';
import { enlaceDeWhatsApp } from '@/club/alumnos/tutores/whatsapp';

export function InvitarPorWhatsApp({
  club,
  alumnoId,
  tutor,
  alCerrar,
}: {
  club: string;
  alumnoId: string;
  tutor: Tutor;
  alCerrar: () => void;
}) {
  const cache = useQueryClient();
  const [telefono, setTelefono] = useState('');
  const [copiado, setCopiado] = useState(false);
  const pedido = useRef(false);

  const enlace = useMutation({
    mutationFn: async () => {
      const r = await api.v1
        .clubs({ club })
        .students({ studentId: alumnoId })
        .guardians({ guardianId: tutor.user_id })
        ['invitation-link'].post();
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onError: (error) => {
      if (aProblema(error).code === 'guardian_already_joined') {
        void cache.invalidateQueries({ queryKey: ['tutores', club, alumnoId] });
      }
    },
  });

  // Una vez, aunque React monte dos veces en desarrollo.
  useEffect(() => {
    if (pedido.current) return;
    pedido.current = true;
    enlace.mutate();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const problema = enlace.isError ? aProblema(enlace.error) : null;
  // Las dos respuestas esperables no son un fallo: son la regla. Se dicen sin rojo.
  const esperable =
    problema?.code === 'invitation_link_unavailable' ||
    problema?.code === 'guardian_already_joined';
  const mensaje = enlace.data?.message ?? '';
  const whatsapp = enlace.data ? enlaceDeWhatsApp(mensaje, telefono) : null;
  const numeroMal = enlace.data !== undefined && whatsapp === null;

  return (
    <div className="hueco hueco-confirmar">
      <h2>Mandar la invitación por WhatsApp</h2>

      <p>
        Se abre WhatsApp con el mensaje para {nombreDeTutor(tutor)} ya escrito,
        y lo envías tú, desde el móvil del club o desde WhatsApp Web. El correo
        que le mandamos sigue valiendo: entrará con el primero que abra.
      </p>

      {enlace.isPending && (
        <Cargando filas={2} etiqueta="Preparando el enlace" />
      )}

      {problema && esperable && (
        <div className="aviso" role="status">
          <strong>{problema.title}.</strong> {problema.detail}
        </div>
      )}

      {problema && !esperable && <PanelError problema={problema} />}

      {enlace.data && (
        <>
          <label htmlFor="whatsapp-movil">Su móvil (opcional)</label>
          <input
            id="whatsapp-movil"
            type="tel"
            inputMode="tel"
            autoComplete="off"
            placeholder="612 345 678"
            value={telefono}
            onChange={(e) => setTelefono(e.target.value)}
            aria-invalid={numeroMal}
            aria-describedby="whatsapp-movil-ayuda"
          />
          <p id="whatsapp-movil-ayuda" className="tenue">
            {numeroMal
              ? 'Revisa el número. Si no es de España, escríbelo con su prefijo: +33…'
              : 'Si lo dejas vacío, eliges el contacto en WhatsApp. Kiap no lo guarda.'}
          </p>

          <h3 style={{ marginBlockStart: '1.2rem' }}>El mensaje</h3>
          <p className="aviso-cuerpo mensaje-de-invitacion">{mensaje}</p>
        </>
      )}

      <div className="hueco-acciones">
        {!problema && (
          <>
            <button
              type="button"
              disabled={whatsapp === null}
              onClick={() => {
                if (whatsapp)
                  window.open(whatsapp, '_blank', 'noopener,noreferrer');
              }}
            >
              Abrir WhatsApp
            </button>
            <button
              type="button"
              className="btn-plano"
              disabled={!enlace.data}
              onClick={() => {
                void navigator.clipboard
                  ?.writeText(mensaje)
                  .then(() => setCopiado(true))
                  // Sin permiso no pasa nada: el texto está en pantalla y se selecciona.
                  .catch(() => setCopiado(false));
              }}
            >
              {copiado ? 'Copiado' : 'Copiar el mensaje'}
            </button>
          </>
        )}
        <button type="button" className="btn-plano" onClick={alCerrar}>
          Cerrar
        </button>
      </div>
    </div>
  );
}
